import React, { useEffect } from 'react';
import Grid from '@mui/material/Grid';
import { useTranslations } from 'hooks/useTranslations';
import { Stack, Typography } from '@mui/material';
import { findInDictionaryList } from 'helpers/dictionary';
import { formatDate, getFullYearsSince } from 'utils/date';
import { ELegalEntityTypes, LETypes } from 'constants/LegalEntityTypes';
import { mergeDictionaries } from 'components/organisms/Dictionaries';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { formatAddress } from 'utils/legalAddress';
import { DetailsTemplate, EDetailsPageTypes } from 'components/templates/DetailsTemplate';
import { StyledSectionTypography } from 'components/organisms/LegalEntityDetails/styles';
import { ILegalEntityData } from 'components/organisms/LegalEntityDetails';
import { IOrgLegalEntityData } from 'components/organisms/OrgLegalEntityDetails';
import {
  ILegalEntityExtendedAddress,
  ILegalEntityLicenceMismatching,
} from 'models/legalEntity';
import { useConfigurationLicence } from 'pages/LicenceConfigurationPage/hooks';
import { EDictionaryTypes, IDictionary } from 'models/dictionary';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { useLang } from 'models/langContext';
import { IOwner } from 'models/licenceConfigurator';
import { EmptyPhotoContainer } from 'components/organisms/EmptyPhotoContainer';
import { useWeaponLicenceModule } from 'hooks/useWeaponLicenceModule';

import { StyledDivider, StyledOwnerDetail, StyledPhoto } from './styles';
import { useLegalEntities } from '../hooks';
import { notApplicableFieldHolder } from '../../LicencesPage/helpers';
import { ERouteLinks } from 'models/route';
import { useNavigate } from 'react-router-dom';
import { EFieldsOptional } from 'pages/SystemSettingsPage/SystemFieldsConfigurationPage/helpers';
import { useSystemFieldsConfiguration } from 'pages/SystemSettingsPage/SystemFieldsConfigurationPage/hooks';

interface ILicenceFormOwnerDetails {
  legalEntity: ILegalEntityData | IOrgLegalEntityData | null;
  owner: IOwner;
  licenceMismatchingLegalEntity: ILegalEntityLicenceMismatching | null;
  primaryAddress: ILegalEntityExtendedAddress | undefined;
  prefixForLE?: string;
  image: any;
  type: LETypes;
}

const LicenceFormOwnerDetails = ({
  legalEntity,
  owner,
  licenceMismatchingLegalEntity,
  primaryAddress,
  prefixForLE,
  image,
  type,
}: ILicenceFormOwnerDetails) => {
  const { t } = useTranslations();
  const { shortDateFormat, unit, getProperties } = useGlobalProperty();
  const { selectedLanguage } = useLang();
  const { hasWeaponLicenceModule } = useWeaponLicenceModule();
  const { getFieldsOptional, hiddenFieldsName } = useSystemFieldsConfiguration();
  const navigate = useNavigate();

  const {
    country,
    organisationCategory,
    organisationCivilianType,
    organisationStateType,
    organisationMilitaryType,
    organisationPoliceType,
  } = useConfigurationLicence();
  const { gender, identificationDocumentType, nationality, loadDictionaries } =
    useLegalEntities();

  useEffect(() => {
    getProperties();
    loadDictionaries([
      EDictionaryTypes.Gender,
      EDictionaryTypes.IdentificationDocumentType,
      EDictionaryTypes.nationality,
      EDictionaryTypes.OrganisationMilitaryType,
      EDictionaryTypes.OrganisationPoliceType,
      EDictionaryTypes.OrganisationCivilianType,
      EDictionaryTypes.OrganisationStateType,
      EDictionaryTypes.OrganisationCategory,
    ]);
    getFieldsOptional();
  }, [selectedLanguage]);

  const renderItemDetail = (dictionary: IDictionary[] | null, dictionaryItemId: string) => {
    if (!legalEntity) return '';
    const legal: ILegalEntityData | IOrgLegalEntityData = legalEntity;
    if (legal) {
      return dictionary?.find(
        (item: IDictionary) =>
          item?.uniqueId === legal[dictionaryItemId as keyof ILegalEntityData],
      )?.name;
    }
    return '';
  };

  return (
    <DetailsTemplate
      title={`${prefixForLE ?? ''} ${
        type === LETypes.individual
          ? `${legalEntity?.firstName} ${legalEntity?.lastName}`
          : `${legalEntity?.organizationName}`
      }    `}
      pageType={EDetailsPageTypes.view}
      actionButtons={[
        {
          text: t('licenceDetails.seeDetails.button'),
          toNewTab: `${ERouteLinks.ViewLegalEntity.replace(
            ':id',
            legalEntity?.uniqueId ?? '',
          )}?type=${type === LETypes.individual ? ELegalEntityTypes.individual : ELegalEntityTypes.organisation}`,
        },
      ]}
    >
      {type === LETypes.individual ? (
        <Grid
          size={{ xs: 12 }}
          sx={{
            display: 'flex',
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            justifyContent: 'center',
            gap: '10px',
          }}
        >
          {hasWeaponLicenceModule() && (
            <Grid
              container
              sx={{
                width: { xs: '100%', md: '270px' },
                ml: -1,
                justifyContent: { xs: 'center', md: 'flex-start' },
              }}
            >
              <StyledPhoto>
                {image ? (
                  <img
                    src={image}
                    alt="ovner_photo"
                    style={{ width: '270px', height: '320px', objectFit: 'cover' }}
                  />
                ) : (
                  <EmptyPhotoContainer />
                )}
              </StyledPhoto>
            </Grid>
          )}
          <Grid
            container
            columnSpacing={2}
            sx={{ rowGap: 2, flex: 1, width: { xs: 12, md: 'auto' } }}
          >
            {owner.individual.legalEntityId && (
              <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t('legalEntityDetails.displayUniqueId.label')}
                  </Typography>
                  <Typography variant="body2">{legalEntity?.displayUniqueId}</Typography>
                </Stack>
              </Grid>
            )}
            {owner.individual?.fullName && (
              <>
                <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                  <Stack>
                    <StyledOwnerDetail
                      warning={!!licenceMismatchingLegalEntity?.firstName}
                      variant="subtitle2"
                      label
                      sx={{ marginBottom: '4px' }}
                    >
                      {t('legalEntityDetails.firstName.label')}
                    </StyledOwnerDetail>
                    <StyledOwnerDetail
                      warning={!!licenceMismatchingLegalEntity?.firstName}
                      variant="body2"
                    >
                      {legalEntity?.firstName}
                    </StyledOwnerDetail>
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                  <Stack>
                    <StyledOwnerDetail
                      warning={!!licenceMismatchingLegalEntity?.middleNames}
                      variant="subtitle2"
                      label
                      sx={{ marginBottom: '4px' }}
                    >
                      {t('legalEntityDetails.middleNames.label')}
                    </StyledOwnerDetail>
                    <StyledOwnerDetail
                      warning={!!licenceMismatchingLegalEntity?.middleNames}
                      variant="body2"
                    >
                      {emptyFieldHolder(legalEntity?.middleNames)}
                    </StyledOwnerDetail>
                  </Stack>
                </Grid>

                <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                  <Stack>
                    <StyledOwnerDetail
                      warning={!!licenceMismatchingLegalEntity?.lastName}
                      variant="subtitle2"
                      label
                      sx={{ marginBottom: '4px' }}
                    >
                      {t('legalEntityDetails.lastName.label')}
                    </StyledOwnerDetail>
                    <StyledOwnerDetail
                      warning={!!licenceMismatchingLegalEntity?.lastName}
                      variant="body2"
                    >
                      {legalEntity?.lastName}
                    </StyledOwnerDetail>
                  </Stack>
                </Grid>
                {!hiddenFieldsName.includes(EFieldsOptional.secondSurname) && (
                  <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                    <Stack>
                      <StyledOwnerDetail
                        warning={!!licenceMismatchingLegalEntity?.secondSurname}
                        variant="subtitle2"
                        label
                        sx={{ marginBottom: '4px' }}
                      >
                        {t('legalEntityDetails.secondSurname.label')}
                      </StyledOwnerDetail>
                      <StyledOwnerDetail
                        warning={!!licenceMismatchingLegalEntity?.secondSurname}
                        variant="body2"
                      >
                        {notApplicableFieldHolder(
                          t,
                          legalEntity?.secondSurname,
                          owner.individual.secondSurname,
                        )}
                      </StyledOwnerDetail>
                    </Stack>
                  </Grid>
                )}
              </>
            )}
            <Grid size={{ xs: 12 }}>
              <StyledDivider />
              <StyledSectionTypography>
                {t('legalEntityDetails.generalInformation')}
              </StyledSectionTypography>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <Stack>
                <StyledOwnerDetail
                  warning={!!licenceMismatchingLegalEntity?.documentType}
                  variant="subtitle2"
                  label
                  sx={{ marginBottom: '4px' }}
                >
                  {t('legalEntityDetails.documentType.label')}
                </StyledOwnerDetail>
                <StyledOwnerDetail
                  warning={!!licenceMismatchingLegalEntity?.documentType}
                  variant="body2"
                >
                  {notApplicableFieldHolder(
                    t,
                    renderItemDetail(
                      identificationDocumentType,
                      'identityDocumentTypeUniqueId',
                    ),
                    owner.individual.documentTypeAndNumber,
                  )}
                </StyledOwnerDetail>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <Stack>
                <StyledOwnerDetail
                  warning={!!licenceMismatchingLegalEntity?.documentNumber}
                  variant="subtitle2"
                  label
                  sx={{ marginBottom: '4px' }}
                >
                  {t('legalEntityDetails.identityDocumentNumber.label')}
                </StyledOwnerDetail>
                <StyledOwnerDetail
                  warning={!!licenceMismatchingLegalEntity?.documentNumber}
                  variant="body2"
                >
                  {notApplicableFieldHolder(
                    t,
                    legalEntity?.identityDocumentNumber,
                    owner.individual.documentTypeAndNumber,
                  )}
                </StyledOwnerDetail>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <Stack>
                <Grid>
                  <StyledOwnerDetail
                    warning={!!licenceMismatchingLegalEntity?.gender}
                    variant="subtitle2"
                    label
                    sx={{ marginBottom: '4px' }}
                  >
                    {t('legalEntityDetails.gender.label')}
                  </StyledOwnerDetail>
                </Grid>
                <StyledOwnerDetail
                  warning={!!licenceMismatchingLegalEntity?.gender}
                  variant="body2"
                  id="gender"
                >
                  {renderItemDetail(gender, 'genderUniqueId')}
                </StyledOwnerDetail>
              </Stack>
            </Grid>
            {!hiddenFieldsName.includes(EFieldsOptional.occupation) && (
              <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                <Stack>
                  <StyledOwnerDetail
                    warning={!!licenceMismatchingLegalEntity?.occupation}
                    variant="subtitle2"
                    label
                    sx={{ marginBottom: '4px' }}
                  >
                    {t('legalEntityDetails.occupation.label')}
                  </StyledOwnerDetail>
                  <StyledOwnerDetail
                    warning={!!licenceMismatchingLegalEntity?.occupation}
                    variant="body2"
                  >
                    {notApplicableFieldHolder(
                      t,
                      legalEntity?.occupation,
                      owner.individual.occupation,
                    )}
                  </StyledOwnerDetail>
                </Stack>
              </Grid>
            )}

            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <Stack>
                <StyledOwnerDetail
                  warning={!!licenceMismatchingLegalEntity?.dateOfBirth}
                  variant="subtitle2"
                  label
                  sx={{ marginBottom: '4px' }}
                >
                  {t('legalEntityDetails.dateOfBirth.label')}
                </StyledOwnerDetail>
                <StyledOwnerDetail
                  warning={!!licenceMismatchingLegalEntity?.dateOfBirth}
                  variant="body2"
                >
                  {notApplicableFieldHolder(
                    t,
                    legalEntity?.dateOfBirth
                      ? `${formatDate(legalEntity.dateOfBirth, shortDateFormat)}${
                          getFullYearsSince(legalEntity.dateOfBirth) > 0
                            ? ` (${getFullYearsSince(legalEntity.dateOfBirth)} ${t(
                                'legalEntity.yearsOld',
                              )})`
                            : ''
                        } `
                      : '',
                    owner.individual.dateOfBirth,
                  )}
                </StyledOwnerDetail>
              </Stack>
            </Grid>
            {!hiddenFieldsName.includes(EFieldsOptional.placeOfBirth) && (
              <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                <Stack>
                  <StyledOwnerDetail
                    warning={!!licenceMismatchingLegalEntity?.placeOfBirth}
                    variant="subtitle2"
                    label
                    sx={{ marginBottom: '4px' }}
                  >
                    {t('legalEntityDetails.placeOfBirth.label')}
                  </StyledOwnerDetail>
                  <StyledOwnerDetail
                    warning={!!licenceMismatchingLegalEntity?.placeOfBirth}
                    variant="body2"
                  >
                    {notApplicableFieldHolder(
                      t,
                      legalEntity?.placeOfBirth,
                      owner.individual.placeOfBirth,
                    )}
                  </StyledOwnerDetail>
                </Stack>
              </Grid>
            )}
            {!hiddenFieldsName.includes(EFieldsOptional.height) && (
              <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                <Stack>
                  <StyledOwnerDetail
                    warning={!!licenceMismatchingLegalEntity?.height}
                    variant="subtitle2"
                    label
                    sx={{ marginBottom: '4px' }}
                  >
                    {t('legalEntityDetails.height.label')}
                  </StyledOwnerDetail>
                  <StyledOwnerDetail
                    warning={!!licenceMismatchingLegalEntity?.height}
                    variant="body2"
                  >
                    {notApplicableFieldHolder(
                      t,
                      legalEntity?.height ? `${legalEntity.height} ${unit} ` : '',
                      owner.individual.height,
                    )}
                  </StyledOwnerDetail>
                </Stack>
              </Grid>
            )}

            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <Stack>
                <StyledOwnerDetail
                  warning={!!licenceMismatchingLegalEntity?.nationality}
                  variant="subtitle2"
                  label
                  sx={{ marginBottom: '4px' }}
                >
                  {t('legalEntityDetails.nationality.label')}
                </StyledOwnerDetail>
                <StyledOwnerDetail
                  warning={!!licenceMismatchingLegalEntity?.nationality}
                  variant="body2"
                >
                  {notApplicableFieldHolder(
                    t,
                    renderItemDetail(nationality, 'nationalityUniqueId'),
                    owner.individual?.nationality,
                  )}
                </StyledOwnerDetail>
              </Stack>
            </Grid>
            {owner.individual.primaryAddress && (
              <Grid size={{ xs: 12, sm: 6, lg: 12 }}>
                <Stack>
                  <StyledOwnerDetail
                    warning={!!licenceMismatchingLegalEntity?.primaryAddress}
                    variant="subtitle2"
                    label
                    sx={{ marginBottom: '4px' }}
                  >
                    {t('legalEntityDetails.address.label')}
                  </StyledOwnerDetail>
                  <StyledOwnerDetail
                    warning={!!licenceMismatchingLegalEntity?.primaryAddress}
                    variant="body2"
                  >
                    {emptyFieldHolder(formatAddress(primaryAddress, country))}
                  </StyledOwnerDetail>
                </Stack>
              </Grid>
            )}
          </Grid>
        </Grid>
      ) : (
        <Grid container columnSpacing={2} sx={{ rowGap: 2 }}>
          {owner.organisation.organisationName && (
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <Stack>
                <StyledOwnerDetail
                  warning={!!licenceMismatchingLegalEntity?.organisationName}
                  variant="subtitle2"
                  label
                  sx={{ marginBottom: '4px' }}
                >
                  {t('legalEntity.licence.name.label')}
                </StyledOwnerDetail>
                <StyledOwnerDetail
                  warning={!!licenceMismatchingLegalEntity?.organisationName}
                  variant="body2"
                >
                  {legalEntity?.organizationName}
                </StyledOwnerDetail>
              </Stack>
            </Grid>
          )}

          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <Stack>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('legalEntity.licence.uniqueID.label')}
              </Typography>
              {notApplicableFieldHolder(
                t,
                legalEntity?.displayUniqueId,
                owner.organisation.legalEntityId,
              )}
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <Stack>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('legalEntity.licence.organisationCategory.label')}
              </Typography>
              {notApplicableFieldHolder(
                t,
                renderItemDetail(organisationCategory, 'organisationCategoryUniqueId'),
              )}
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <Stack>
              <StyledOwnerDetail
                warning={!!licenceMismatchingLegalEntity?.organisationTypes}
                variant="subtitle2"
                label
                sx={{ marginBottom: '4px' }}
              >
                {t('legalEntity.licence.organisationType.label')}
              </StyledOwnerDetail>
              <StyledOwnerDetail
                warning={!!licenceMismatchingLegalEntity?.organisationTypes}
                variant="body2"
              >
                {notApplicableFieldHolder(
                  t,
                  findInDictionaryList(
                    legalEntity?.organisationTypeUniqueIds,
                    mergeDictionaries([
                      organisationStateType,
                      organisationCivilianType,
                      organisationMilitaryType,
                      organisationPoliceType,
                    ]),
                  )?.join(', ') || '',
                  owner.organisation.organisationType,
                )}
              </StyledOwnerDetail>
            </Stack>
          </Grid>
          {!hiddenFieldsName.includes(EFieldsOptional.departmentName) && (
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <Stack>
                <StyledOwnerDetail
                  warning={!!licenceMismatchingLegalEntity?.departmentName}
                  variant="subtitle2"
                  label
                  sx={{ marginBottom: '4px' }}
                >
                  {t('legalEntity.licence.departmentName.label')}
                </StyledOwnerDetail>
                <StyledOwnerDetail
                  warning={!!licenceMismatchingLegalEntity?.departmentName}
                  variant="body2"
                >
                  {notApplicableFieldHolder(
                    t,
                    legalEntity?.departmentName,
                    owner.organisation.departmentName,
                  )}
                </StyledOwnerDetail>
              </Stack>
            </Grid>
          )}
          {owner.organisation.primaryAddress && (
            <Grid size={{ xs: 12, sm: 6, lg: 12 }}>
              <Stack>
                <StyledOwnerDetail
                  warning={!!licenceMismatchingLegalEntity?.primaryAddress}
                  variant="subtitle2"
                  label
                  sx={{ marginBottom: '4px' }}
                >
                  {t('legalEntityDetails.address.label')}
                </StyledOwnerDetail>
                <StyledOwnerDetail
                  warning={!!licenceMismatchingLegalEntity?.primaryAddress}
                  variant="body2"
                >
                  {emptyFieldHolder(formatAddress(primaryAddress, country))}
                </StyledOwnerDetail>
              </Stack>
            </Grid>
          )}
        </Grid>
      )}
    </DetailsTemplate>
  );
};

export { LicenceFormOwnerDetails };
